import type { BlendSettings } from '~/domain/viewport/fusionBlend';
import type { Environment } from '../../data/Environment';
import type { ImageObject } from '../../data/image/ImageObject';

/**
 * Which of a fused pair a view draws. The pair lives in one Environment, so
 * two views of it can show different sides of the same fusion: the primary
 * beside its registered secondary, rather than the two blended into one.
 * Every view draws the blend unless told otherwise.
 */
export enum FusedImageDisplay {
    Blended = 'blended',
    PrimaryOnly = 'primary-only',
    SecondaryOnly = 'secondary-only',
}

/**
 * What a view reads of its Environment to tell the image it draws: the
 * fused pair, and the aligned images a view may show alone.
 */
type ShownImages = Pick<Environment, 'image' | 'secondaryImage'> &
    Partial<Pick<Environment, 'alignedImage'>>;

/**
 * A fusion a view draws in place of its Environment's own: the
 * Environment's image with a partner the Environment holds aligned, under
 * `alignedImageKey` (none: the view fuses nothing), mixed by `blend`. Two
 * views of one Environment can so draw two different pairs.
 */
export interface ViewFusion {
    readonly alignedImageKey: string | undefined;
    readonly blend: BlendSettings;
}

/** The pair a view with its own fusion draws, read as an Environment's pair is. */
export type ViewFusedPair = ShownImages &
    Pick<Environment, 'alpha' | 'registration'>;

/**
 * The fused pair `view` draws: its Environment's own, or for a view given
 * its own fusion, the Environment's image with the view's partner, placed
 * by the partner's registration and mixed by the view's blend. Read on
 * every call: a partner released or re-placed since shows as it now is.
 */
export function fusedPairOf<E extends ShownImages>(view: {
    readonly environment: E &
        Partial<Pick<Environment, 'alignedImageRegistration'>>;
    readonly viewFusion?: ViewFusion;
}): E | ViewFusedPair {
    const { environment, viewFusion } = view;
    return viewFusion ? ownFusedPair(environment, viewFusion) : environment;
}

/** The pair `viewFusion` makes of `environment`'s image, as {@link fusedPairOf} reads it. */
export function ownFusedPair(
    environment: ShownImages &
        Partial<Pick<Environment, 'alignedImageRegistration'>>,
    viewFusion: ViewFusion
): ViewFusedPair {
    const key = viewFusion.alignedImageKey;
    return {
        image: environment.image,
        secondaryImage:
            key === undefined ? undefined : environment.alignedImage?.(key),
        alignedImage: (alignedKey) => environment.alignedImage?.(alignedKey),
        alpha: viewFusion.blend.alpha,
        registration:
            key === undefined
                ? undefined
                : environment.alignedImageRegistration?.(key),
    };
}

/** What one view hands the image renderer: the two slots and their blend. */
export interface FusedImageSlots {
    readonly primaryImage: ImageObject | undefined;
    readonly secondaryImage: ImageObject | undefined;
    readonly alpha: number;
    /**
     * Whether the Environment's own view mode applies. False means plain
     * alpha-over: checkerboard interleaves the pair, so with one slot showing
     * it would punch holes in the only image on screen.
     */
    readonly showsBlend: boolean;
    /**
     * Which of the Environment's colour masks the primary slot takes. The
     * secondary drawn alone keeps the secondary's mask, so it looks as it
     * does in the blend. An aligned image is in no blend and takes no tint:
     * `'none'`.
     */
    readonly primarySlotMask: 'primary' | 'secondary' | 'none';
}

/**
 * The image a view of a fused pair draws on its own, or the primary for a
 * blended view. Undefined when the side asked for is not loaded, so a
 * secondary-only view of an unfused Environment draws nothing rather than
 * the primary.
 *
 * A view given `alignedImageKey` draws that aligned image alone, whatever
 * its display, and nothing once the image is no longer held.
 */
export function displayedImage(
    display: FusedImageDisplay,
    environment: ShownImages,
    alignedImageKey?: string
): ImageObject | undefined {
    if (alignedImageKey !== undefined) {
        return environment.alignedImage?.(alignedImageKey);
    }
    return display === FusedImageDisplay.SecondaryOnly
        ? environment.secondaryImage
        : environment.image;
}

/**
 * Resolves what a view draws of a fused pair.
 *
 * `Blended` keeps the Environment's own blend. Either single-side display
 * takes the plain alpha-over path with one image in the primary slot and
 * the secondary slot empty. The secondary keeps its own registered grid, so
 * drawn alone it still lands where the blend placed it, and the renderer
 * never paints the primary beneath it: a PET secondary over a CT would
 * otherwise show the CT wherever the PET has no data.
 */
export function resolveFusedImageSlots(
    display: FusedImageDisplay,
    environment: ShownImages & Pick<Environment, 'alpha'>,
    alignedImageKey?: string
): FusedImageSlots {
    if (alignedImageKey !== undefined) {
        return {
            primaryImage: displayedImage(display, environment, alignedImageKey),
            secondaryImage: undefined,
            alpha: 1,
            showsBlend: false,
            primarySlotMask: 'none',
        };
    }
    if (display === FusedImageDisplay.Blended) {
        return {
            primaryImage: environment.image,
            secondaryImage: environment.secondaryImage,
            alpha: environment.alpha,
            showsBlend: true,
            primarySlotMask: 'primary',
        };
    }
    return {
        primaryImage: displayedImage(display, environment),
        secondaryImage: undefined,
        alpha: 1,
        showsBlend: false,
        primarySlotMask:
            display === FusedImageDisplay.SecondaryOnly
                ? 'secondary'
                : 'primary',
    };
}

/**
 * The series a window/level drag on a view changes: the image the view
 * draws alone, or the Environment's own windowing target for the blend.
 */
export function windowingSeriesUidFor(
    display: FusedImageDisplay,
    environment: ShownImages & Pick<Environment, 'activeWindowingSeriesUid'>,
    alignedImageKey?: string
): string | undefined {
    return display === FusedImageDisplay.Blended &&
        alignedImageKey === undefined
        ? environment.activeWindowingSeriesUid
        : displayedImage(display, environment, alignedImageKey)?.parent
              .seriesUid;
}
